<template>
  <custom-modal
    class="add-table-dialog"
    modal-width="25rem"
    body-padding="0 24px 0px"
    :close-on-backdrop="false"
    @click="handleFocus(-1)"
    @close="closeModal"
  >
    <template #header>
      {{ showTableSettings ? $t("table.editTable") : $t("table.addTable") }}
    </template>

    <template #body>
      <div class="kk__tableDialog">
        <form
          v-if="showOtherTableParts"
          class="kk__tableDialog--form"
          @keydown="handleKeyDown"
          @submit.prevent="action"
        >
          <div class="kk__tableDialog--form-group">
            <fieldset class="kk__tableDialog--form-field">
              <legend>{{ $t("table.range") }}</legend>
              <range-selector :attrs="rangeSelectorAttrs" @passRange="range = $event" />
            </fieldset>
            <custom-icon
              :class-name="rangeSelectorCollapse ? icons.ChevronDown : icons.ChevronUp"
              class="kk__tableDialog--collapse"
              @click="toggleSelector"
            />
          </div>

          <div class="kk__tableDialog--form-group">
            <fieldset class="kk__tableDialog--form-field">
              <legend>{{ $t("table.tableName") }}</legend>
              <input
                ref="tableName"
                placeholder="eg: Table_1"
                class="kk__tableDialog--form-input"
                :value="localTableName"
                type="text"
                @input="localTableName = $event.target.value"
                @click="handleClick"
              />
            </fieldset>
          </div>

          <div class="kk__tableDialog--checkboxes">
            <label>
              <input
                v-model="applyFormulaToEntireColumn"
                type="checkbox"
                @keydown.space.prevent="toggleCheckbox('applyFormulaToEntireColumn')"
                @click="handleClick"
              />
              {{ $t("table.applyFormula") }}
            </label>
            <label>
              <input
                v-model="allowDynamicExpansion"
                type="checkbox"
                @keydown.space.prevent="toggleCheckbox('allowDynamicExpansion')"
                @click="handleClick"
              />
              {{ $t("table.applyExpansion") }}
            </label>
            <!-- <label>
              <input v-model="filterButtonVisible" type="checkbox" />
              Filter Button
            </label> -->
          </div>

          <div>
            <fieldset class="kk__tableDialog--styles">
              <legend>{{ $t("table.styles") }}</legend>
              <div class="kk__tableDialog--icons">
                <table-colors
                  v-for="item in predifinedIcons"
                  :key="item"
                  :style-id="item"
                  :selected="item == selectedStyle"
                  @click="handleStyleClick(item)"
                />
              </div>
            </fieldset>
          </div>

          <div class="kk__tableDialog--btns">
            <!-- TODO: Add validation for button -->
            <custom-button button-class="btn btn-default" button-type="button" @click="closeModal">
              {{ $t("actions.cancel") }}
            </custom-button>

            <custom-button
              class="kk__tableDialog--btns-submit"
              button-class="btn btn-default btn-primary"
              button-type="submit"
              :mode="!isRangeValid || !isTableTitleValid"
            >
              {{ showTableSettings ? $t("actions.update") : $t("actions.add") }}
            </custom-button>
          </div>
        </form>
        <form v-else class="structureTable" @submit.prevent="action">
          <div class="kk__tableDialog--form-group">
            <fieldset class="kk__tableDialog--form-field">
              <legend>{{ $t("table.range") }}</legend>
              <range-selector :attrs="rangeSelectorAttrs" @passRange="range = $event" />
            </fieldset>
            <custom-icon
              :class-name="rangeSelectorCollapse ? icons.ChevronDown : icons.ChevronUp"
              class="kk__tableDialog--collapse"
              @click="toggleSelector"
            />
          </div>
        </form>
      </div>
    </template>
    <template #footer>
      <span></span>
    </template>
  </custom-modal>
</template>

<script>
import DesignerMixin from "../DesignerMixin";
import CustomButton from "../../commons/CustomButton.vue";
import customModal from "../../commons/CustomModal.vue";
import CustomIcon from "../../commons/CustomIcon.vue";
import { v4 as uuidv4 } from "uuid";
import TableColors from "../../commons/TableColors.vue";
import { icons } from "../../../utils/icons";
import { tableTitleValidation, isRange, convertToAbsolute } from "../../../utils/util";
import RangeSelector from "../../commons/RangeSelector.vue"; // Assuming rangeSelector component

export default {
  components: {
    CustomButton,
    TableColors,
    customModal,
    RangeSelector,
    CustomIcon,
  },
  mixins: [DesignerMixin],
  data() {
    return {
      editingTable: null,
      localTableName: "table_1",
      headerVisible: true,
      filterButtonVisible: true,
      totalRowEnabled: true,
      allowDynamicExpansion: true,
      applyFormulaToEntireColumn: true,
      selectedStyle: 0,
      isOpen: true,
      icons: icons,
      range: "",
      activeCellId: "",
      sequenceValue: 1,
      predifinedIcons: [0, 1, 2, 3],
      currentFocusIndex: null,
      rangeSelectorAttrs: {
        placeholder: "eg: $H$25:I$29",
        class: "kk__tableDialog--form-input",
        type: "text",
      },
    };
  },
  computed: {
    isTableTitleValid() {
      return tableTitleValidation(this.localTableName);
    },
    showTableSettings() {
      return this.$store.state.table.activeTable;
    },
    table() {
      return this.$store.state.table.activeTable;
    },
    isRangeValid() {
      return this.range.trim() !== "" && isRange(this.range);
    },
    showDialog: {
      get() {
        return this.$store.state.table.showAddTableDialog;
      },
      set(newValue) {
        return this.$store.dispatch("table/setShowAddTableDialog", newValue);
      },
    },
    showOtherTableParts() {
      return this.rangeSelectorCollapse && !this.startDragging;
    },
  },
  watch: {
    table: {
      handler(newTable) {
        if (newTable) {
          this.editingTable = newTable;
          this.localTableName = newTable?.tableName;
          this.headerVisible = newTable?.headerVisible;
          this.filterButtonVisible = newTable?.filterButtonVisible;
          this.totalRowEnabled = newTable?.totalRowEnabled;
          this.allowDynamicExpansion = newTable?.allowDynamicExpansion;
          this.applyFormulaToEntireColumn = newTable?.applyFormulaToEntireColumn;
          this.selectedStyle = newTable?.styleId ?? "1";
          this.range = convertToAbsolute(newTable.tableRange);
        } else {
          let oldExpreession = this.$store.state.table.groupedTableNamedExpressions[this.localTableName.toLowerCase()];
          let sequenceValue = this.$store.state.table.sequenceValue;
          while (oldExpreession) {
            this.localTableName = `Table_${++sequenceValue}`;
            oldExpreession = this.$store.state.table.groupedTableNamedExpressions[this.localTableName.toLowerCase()];
          }
          this.sequenceValue = sequenceValue;
        }
      },
    },
  },
  mounted() {
    if (!this.rangeSelectorCollapse) this.$store.dispatch("cells/setRangeSelectorCollapse", true);
    this.activeCellId = this.$store.state.cells.activeCellId;
    if (this.table) {
      this.localTableName = this.table.tableName;
      this.totalRowEnabled = this.table.totalRowEnabled;
      this.allowDynamicExpansion = this.table.allowDynamicExpansion;
      this.applyFormulaToEntireColumn = this.table.applyFormulaToEntireColumn;
      this.selectedStyle = this.table.styleId;
      this.editingTable = this.table;
      this.range = convertToAbsolute(this.table.tableRange);
      if (!this.selectedStyle) {
        this.selectedStyle = 1;
      }
    } else {
      let oldExpreession = this.$store.state.table.groupedTableNamedExpressions[this.localTableName.toLowerCase()];
      let sequenceValue = this.$store.state.table.sequenceValue;
      while (oldExpreession) {
        this.localTableName = `Table_${++sequenceValue}`;
        oldExpreession = this.$store.state.table.groupedTableNamedExpressions[this.localTableName.toLowerCase()];
      }
      this.sequenceValue = sequenceValue;
    }
  },

  methods: {
    toggleSelector() {
      const value = this.$store.state.cells.rangeSelectorCollapse;
      this.$store.dispatch("cells/setRangeSelectorCollapse", !value);
    },
    handleClick() {
      const focusable = this.getFocusableElements();
      const activeIndex = focusable.indexOf(document.activeElement);
      if (activeIndex !== -1) this.currentFocusIndex = activeIndex;
    },
    toggleCheckbox(checkboxName) {
      // Toggle the value of the checkbox
      this[checkboxName] = !this[checkboxName];
    },
    getFocusableElements() {
      // Collect all focusable elements in the form
      return [...this.$el.querySelectorAll('button, [href], input, select, textarea, div[tabindex="0"]')].filter(
        (el) => !el.disabled && !el.hasAttribute("disabled") && el.offsetParent !== null
      );
    },

    navigateStyles(direction) {
      const currentIndex = this.predifinedIcons.indexOf(this.selectedStyle);
      let newIndex = (currentIndex + direction + this.predifinedIcons.length) % this.predifinedIcons.length;
      this.selectedStyle = this.predifinedIcons[newIndex];

      this.$nextTick(() => {
        const focusableElement = this.$el.querySelector(`.kk__tableDialog--icons [tabindex="0"]`);
        focusableElement?.focus();
      });
    },

    handleStylesNavigation(event) {
      // If focus is on the styles container
      switch (event.key) {
        case "ArrowRight":
          this.navigateStyles(1); // Navigate to the next style item
          break;
        case "ArrowLeft":
          this.navigateStyles(-1); // Navigate to the previous style item
          break;
        case "Tab":
          event.preventDefault();
          this.handleTabNavigation(event.shiftKey); // Move to the next main form element
          break;
      }
    },

    handleStyleClick(item) {
      this.currentFocusIndex = 4;
      this.selectStyle(item);
      this.$nextTick(() => {
        const focusableElement = this.$el.querySelector("div[tabindex='0']");
        focusableElement?.focus();
      });
    },

    handleKeyDown(e) {
      e.stopPropagation();
      const stylesContainer = this.$el.querySelector(".kk__tableDialog--styles");
      const isFocusInsideStyles = stylesContainer.contains(document.activeElement);

      switch (e.key) {
        case "Tab":
          e.preventDefault();
          this.handleTabNavigation(e.shiftKey);
          break;
        case "Escape":
          e.preventDefault();
          this.closeModal();
          break;
        case "Enter":
          this.action();
          break;
        case "ArrowRight":
        case "ArrowLeft":
          if (isFocusInsideStyles) {
            const direction = e.key === "ArrowRight" ? 1 : -1;
            this.navigateStyles(direction);
          }
          break;
      }
    },

    handleFocus(index) {
      const focusable = this.getFocusableElements();
      if (index < 0) {
        const activeIndex = focusable.indexOf(document.activeElement);
        if (activeIndex !== -1) this.currentFocusIndex = activeIndex;
        focusable[this.currentFocusIndex]?.focus();
      } else {
        focusable[index]?.focus();
      }
    },

    handleTabNavigation(isShift = false) {
      const focusable = this.getFocusableElements();
      this.currentFocusIndex = (this.currentFocusIndex + (isShift ? -1 : 1) + focusable.length) % focusable.length;
      this.handleFocus(this.currentFocusIndex);
    },

    moveButtonFocus() {
      const focusableButtons = [...this.$el.querySelectorAll("button")].filter(
        (el) => !el.disabled && !el.hasAttribute("disabled") && el.offsetParent !== null
      );

      const currentIndex = focusableButtons.indexOf(document.activeElement);
      const nextIndex = currentIndex === 1 ? 0 : 1;

      focusableButtons[nextIndex].focus();
      this.currentFocusIndex = nextIndex === 0 ? 3 : 4;
    },

    selectStyle(style) {
      this.selectedStyle = style;
    },
    clicked() {
      this.isOpen = !this.isOpen;
    },
    async action() {
      this.showTableSettings ? await this.updateSettings() : await this.submit();
    },
    async updateSettings() {
      const updatedTable = {
        ...this.editingTable,
        tableName: this.localTableName,
        headerVisible: this.headerVisible,
        filterButtonVisible: this.filterButtonVisible,
        styleId: this.selectedStyle + "",
        totalRowEnabled: this.totalRowEnabled,
        applyFormulaToEntireColumn: this.applyFormulaToEntireColumn,
        allowDynamicExpansion: this.allowDynamicExpansion,
        tableRange: this.range,
      };

      this.$store.dispatch("table/updateTableDetails", {
        updatedTable: { ...updatedTable },
        oldTable: { ...this.editingTable },
      });
      this.closeModal();
    },
    closeModal(event) {
      if (event) {
        event.stopPropagation();
      }
      this.showDialog = false;
    },

    async submit() {
      const sheetId = this.$store.state.sheets.currentSheet.id;
      const designId = this.$store.state.cells.current.id;

      const tableData = {
        _id: uuidv4(),
        tableName: this.localTableName,
        tableRange: this.range,
        sheetId: sheetId,
        designId: designId,
        sequenceValue: this.sequenceValue,
        headerVisible: this.headerVisible, // default value
        totalRowEnabled: this.totalRowEnabled, // default value
        applyFormulaToEntireColumn: this.applyFormulaToEntireColumn,
        allowDynamicExpansion: this.allowDynamicExpansion,
        view: {
          filterApplied: null,
          sortApplied: {},
        },
        styleId: this.selectedStyle + "",
      };
      this.$store.dispatch("table/createTable", tableData);
      this.closeModal();
    },
  },
};
</script>

<style lang="less" scoped>
@import "../../../assets/styles/variables";

:deep(*:focus) {
  outline: 0.5px solid @kk-corner-header-color;
  transition: outline ease, box-shadow ease;
}

.kk {
  &__tableDialog {
    legend {
      font-size: 0.9rem;
      font-weight: 500;
      color: #0056ce;
    }

    fieldset {
      border-radius: 3px;
      background: rgba(238, 238, 238, 0.1);
      border: 1px solid rgba(8, 50, 119, 0.2);
      padding-left: 0.7rem;
    }

    &--collapse {
      position: absolute;
      top: 50%;
      right: 0px;
      transform: translate(-50%, -25%);
    }

    &--form {
      display: flex;
      flex-direction: column;
      gap: 1rem;

      &-group:first-child {
        position: relative;
      }

      &-group {
        width: 100%;
      }

      &-field {
        padding-bottom: 5px;
        &:focus-within {
          border: 1.5px solid #0056ce;
        }
      }
      &-input {
        width: 100%;
        color: rgba(8, 50, 119);

        ::placeholder {
          color: rgba(8, 50, 119, 0.35);
        }

        &:focus {
          outline: none;
          border: none;
        }
      }
    }

    &--checkboxes {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 0.6rem;

      label {
        display: flex;
        align-items: center;
        gap: 0.5rem;
      }

      /* Hide the default checkbox */
      input[type="checkbox"] {
        appearance: none; /* Removes the default styling */
        -webkit-appearance: none; /* Safari support */
        width: 16px;
        height: 16px;
        border: 1px solid #98a5bb;
        border-radius: 3px;
        cursor: pointer;
        outline: none;
      }

      /* Focused state */
      input[type="checkbox"]:focus {
        outline: 0.5px solid @kk-corner-header-color;
      }

      /* Unchecked state */
      input[type="checkbox"] {
        background-color: @kk-color-white;
      }

      /* Checked state */
      input[type="checkbox"]:checked {
        border: none;
        background-color: @kk-color-primary-blue; /* Checkbox color */
      }

      /* Tick styling */
      input[type="checkbox"]:checked::after {
        content: "";
        display: block;
        width: 16px;
        height: 16px;
        background: url("@/assets/checkmark.svg");
        background-size: cover;
      }
    }

    &--styles {
      padding: 1rem 0.7rem;
    }

    &--btns {
      display: flex;
      gap: 1rem;
      button {
        flex: 1;
        height: 30px;
      }
      button:first-child {
        color: @kk-color-primary-blue;
        background: rgba(196, 206, 221, 0.3);
      }
    }

    &--icons {
      display: flex;
      gap: 0.7rem;
      align-items: center;
    }
  }
}
</style>
